import {
  ChatPanel,
  JourneyProfilePanel,
  JourneyProvider,
  type JourneyProviderProps,
  useChatClient,
  useJourney,
} from "@health/sdk-react";
import type { Brand } from "@health/types";
import { useStylesConfigSync } from "@health/ui";
import type { UIMessage } from "ai";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";

// Use JourneyProvider's prop types to avoid importing @health/core server modules
type JourneyConfig = JourneyProviderProps["config"];
type JourneySpecFields = JourneyProviderProps["specFields"];

import { setExperienceVariant } from "@/lib/debug/experience-variant";

// Conditionally define DebugPanel lazy loader
// Vite tree-shakes the import() when VITE_ENABLE_DEBUG_COMPONENTS is not "true"
const DebugPanelLoader =
  import.meta.env.VITE_ENABLE_DEBUG_COMPONENTS === "true"
    ? lazy(() => import("../debug/DebugPanel").then((m) => ({ default: m.DebugPanel })))
    : null;

/**
 * Sync URL ?variant= param to EXPERIENCE_VARIANT cookie on mount.
 * This ensures the server can read the variant on API requests.
 */
function useSyncVariantFromUrl() {
  useEffect(() => {
    const urlVariant = new URLSearchParams(window.location.search).get("variant");
    if (urlVariant) {
      setExperienceVariant(urlVariant);
    }
  }, []);
}

/**
 * Fetches journey config from the experience API and wraps children in JourneyProvider.
 * Lives inside ChatProvider so it can use useChatClient().
 */
function JourneyConnector({
  children,
  messages,
  brand,
  category,
  surfaceIntent,
}: {
  children: React.ReactNode;
  messages: UIMessage[];
  brand?: Brand;
  category?: string;
  surfaceIntent?: "browsing" | "focused" | "comparing";
}) {
  const client = useChatClient();
  const [config, setConfig] = useState<JourneyConfig>([]);
  const [specFields, setSpecFields] = useState<JourneySpecFields>([]);
  const [initialAnswered, setInitialAnswered] = useState<Record<string, unknown> | undefined>();

  useEffect(() => {
    const sessionId = localStorage.getItem("chatAnonymousId") || "default";
    client
      .getExperience(
        {
          sessionId,
          siteId: brand,
          startingCategory: category,
          surfaceIntent,
        },
        { includeAnsweredFields: true },
      )
      .then((exp) => {
        const variant = exp.variant as Record<string, unknown>;
        const journeySections = (variant.journey_sections as JourneyConfig) ?? [];
        const spec = exp.spec as Record<string, unknown>;
        const fields = (spec.fields as JourneySpecFields) ?? [];
        // Hydrate answered fields from patient model (DB) for page-reload progress
        const answered = (exp as Record<string, unknown>).answeredFields as
          | Record<string, unknown>
          | undefined;
        setConfig(journeySections);
        setSpecFields(fields);
        setInitialAnswered(answered);
      })
      .catch((err) => {
        if (import.meta.env.DEV) console.warn("[JourneyConnector] getExperience failed", err);
      });
  }, [client, brand, category, surfaceIntent]);

  if (config.length === 0) {
    return <>{children}</>;
  }

  return (
    <JourneyProvider
      messages={messages}
      config={config}
      specFields={specFields}
      initialAnswered={initialAnswered}
    >
      {children}
      <JourneyOverlay />
    </JourneyProvider>
  );
}

/** Renders the JourneyProfilePanel by consuming useJourney() context. */
function JourneyOverlay() {
  const journey = useJourney();
  if (!journey) return null;

  return (
    <JourneyProfilePanel
      isOpen={journey.isOpen}
      onClose={journey.closePanel}
      sections={journey.sections}
      synthesis={journey.synthesis}
    />
  );
}

export function PrototypeChatSurface({
  enableLoopDetection = false,
  brand,
  category,
  surfaceIntent,
  onToolCall,
}: {
  enableLoopDetection?: boolean;
  brand?: Brand;
  category?: string;
  surfaceIntent?: "browsing" | "focused" | "comparing";
  onToolCall?: (event: { toolName: string; args: Record<string, unknown> }) => void;
}) {
  useSyncVariantFromUrl();
  useStylesConfigSync();

  const [journeyMessages, setJourneyMessages] = useState<UIMessage[]>([]);

  const handleDebugPanel = useCallback(
    (
      props: Parameters<NonNullable<React.ComponentProps<typeof ChatPanel>["renderDebugPanel"]>>[0],
    ) => {
      if (!DebugPanelLoader) return null;

      const wrappedOnSiteIdChange = (id: string) => {
        document.documentElement.setAttribute("data-brand", id);
        props.onSiteIdChange(id);
      };
      const serviceUrl = import.meta.env.VITE_COMPANION_SERVICE_URL ?? "";
      return (
        <Suspense fallback={null}>
          <DebugPanelLoader
            {...props}
            onSiteIdChange={wrappedOnSiteIdChange}
            enableLoopDetection={enableLoopDetection}
            baseUrl={serviceUrl}
          />
        </Suspense>
      );
    },
    [enableLoopDetection],
  );

  return (
    <JourneyConnector
      messages={journeyMessages}
      brand={brand}
      category={category}
      surfaceIntent={surfaceIntent}
    >
      <ChatPanel
        showRecap
        renderDebugPanel={handleDebugPanel}
        initialSiteId={brand}
        initialStartingCategory={category}
        surfaceIntent={surfaceIntent}
        onToolCall={onToolCall}
        onMessagesChange={setJourneyMessages}
      />
    </JourneyConnector>
  );
}
